import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, X, FileText, CheckCircle2, AlertCircle, PlayCircle, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Cross-paper search dialog for the Written Management page.
 *
 * Why a portal+raw-div instead of shadcn Dialog?
 * Search modals are heavy: the input must keep focus while results stream in,
 * the popover should not auto-close on outside click during result hover,
 * and we want full keyboard navigation (↑/↓/Enter) without Radix's focus
 * trap interfering. A focused 80-line component keeps that contract tight.
 */
const WrittenSearchModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  // Reset state on each open + autofocus input
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActiveIndex(0);
      setLoading(false);
      // Defer focus until the modal has actually painted
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Debounced search — 250ms after last keystroke
  useEffect(() => {
    if (!open) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceTimer.current = setTimeout(async () => {
      try {
        const resp = await axios.get(`${API}/api/written/admin/questions/search`, {
          params: { q: query.trim(), limit: 25 }
        });
        setResults(resp.data.results || []);
        setActiveIndex(0);
      } catch (e) {
        // Silently surface empty results on error — toast would be noisy
        // for in-progress typing.
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceTimer.current);
  }, [query, open]);

  const navigateToResult = (r) => {
    if (!r) return;
    if (r.paper_id) {
      navigate(`/admin/written/${r.paper_id}?openQuestion=${encodeURIComponent(r.id)}`);
      onClose?.();
    }
  };

  // Keyboard nav — ↑/↓/Enter/Escape
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { onClose?.(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      navigateToResult(results[activeIndex]);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 flex items-start justify-center pt-[10vh] px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="written-search-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {/* Header / input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search written questions by ID, text, or answer…"
            className="flex-1 outline-none text-sm placeholder-gray-400"
            data-testid="written-search-input"
          />
          {loading && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1"
            aria-label="Close"
            data-testid="written-search-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto" data-testid="written-search-results">
          {!query.trim() && (
            <div className="px-6 py-10 text-center text-sm text-gray-400">
              Start typing to search across all written questions and papers.
              <div className="mt-2 text-xs">
                Tip: paste a full Question ID (e.g.{' '}
                <code className="bg-gray-100 px-1 rounded">meo2-func-011-writ-000003</code>) for an exact jump.
              </div>
            </div>
          )}

          {query.trim() && !loading && results.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-gray-400" data-testid="written-search-empty">
              No matches for <span className="font-mono">{query}</span>.
            </div>
          )}

          {results.map((r, idx) => (
            <button
              key={r.id}
              type="button"
              onClick={() => navigateToResult(r)}
              onMouseEnter={() => setActiveIndex(idx)}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-blue-50 focus:outline-none ${idx === activeIndex ? 'bg-blue-50' : ''}`}
              data-testid={`written-search-result-${r.id}`}
            >
              {/* Top row — ID chip + meta badges */}
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <code className="text-[11px] font-mono bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded">
                  {r.id}
                </code>
                {r.match_source === 'id' && (
                  <Badge variant="static" className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0">
                    ID match
                  </Badge>
                )}
                {r.has_answer ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                    <CheckCircle2 className="w-3 h-3" />
                    Answered
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-700">
                    <AlertCircle className="w-3 h-3" />
                    No answer
                  </span>
                )}
                {r.has_video && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-purple-700">
                    <PlayCircle className="w-3 h-3" />
                    Video
                  </span>
                )}
                {r.appearances_count > 1 && (
                  <Badge variant="static" className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0">
                    {r.appearances_count} papers
                  </Badge>
                )}
              </div>

              {/* Question text snippet */}
              <div className="text-sm text-gray-800 line-clamp-2">
                {r.question_text || <span className="italic text-gray-400">(no question text)</span>}
              </div>

              {/* Bottom row — paper + function context */}
              <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
                {r.paper_label && (
                  <span className="inline-flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {r.paper_label}
                  </span>
                )}
                {r.grade && <span>· {r.grade}</span>}
                {r.function_label && <span className="truncate">· {r.function_label}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Footer hints */}
        <div className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <span>
            <kbd className="px-1 bg-white border border-gray-200 rounded">↑</kbd>{' '}
            <kbd className="px-1 bg-white border border-gray-200 rounded">↓</kbd> navigate ·{' '}
            <kbd className="px-1 bg-white border border-gray-200 rounded">↵</kbd> open ·{' '}
            <kbd className="px-1 bg-white border border-gray-200 rounded">Esc</kbd> close
          </span>
          {results.length > 0 && <span>{results.length} result{results.length === 1 ? '' : 's'}</span>}
        </div>
      </div>
    </div>
  );
};

export default WrittenSearchModal;
